import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Loader2, 
  AlertCircle, 
  Trash2, 
  BarChart3,
  Play,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { ScoringValidationRun } from '../types/evaluation';
import { EvaluationApiService } from '../services/evaluationApi';
import { AppLayout } from './AppLayout';
import { formatCost } from '../utils/costUtils';

const STATUS_CONFIG = {
  Pending: { 
    label: 'Pending', 
    color: 'bg-gray-100 text-gray-700', 
    icon: Clock,
  },
  Running: { 
    label: 'Running', 
    color: 'bg-blue-100 text-blue-700', 
    icon: Play,
  },
  Completed: { 
    label: 'Completed', 
    color: 'bg-green-100 text-green-700', 
    icon: CheckCircle,
  },
  Failed: { 
    label: 'Failed', 
    color: 'bg-red-100 text-red-700', 
    icon: XCircle,
  },
} as const;

export const ScoringValidationListPage = () => {
  const navigate = useNavigate();
  const evaluationApi = EvaluationApiService.getInstance();

  const [runs, setRuns] = useState<ScoringValidationRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [testNames, setTestNames] = useState<Record<string, string>>({});
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    loadRuns();
  }, []);

  // Auto-refresh every 5 seconds while any run is Running
  useEffect(() => {
    const hasRunning = runs.some(r => r.status === 'Running');
    setAutoRefresh(hasRunning);
  }, [runs]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadRuns(false); // Don't show loading indicator during auto-refresh
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadRuns = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const data = await evaluationApi.listScoringValidationRuns();
      setRuns(data);

      // Load test names for runs that need them
      const testIds = [...new Set(data.map(r => r.sourceTestId))];
      const missingTestIds = testIds.filter(id => !(id in testNames));

      if (missingTestIds.length > 0) {
        const tests = await evaluationApi.listEvaluationTests(true);
        const nameMap: Record<string, string> = {};
        for (const test of tests) {
          nameMap[test.id] = test.name;
        }
        setTestNames(prev => ({ ...prev, ...nameMap }));
      }
    } catch (err: any) {
      console.error('Failed to load validation runs:', err);
      setError(err.message || 'Failed to load validation runs');
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  const handleDelete = async (runId: string, runName: string) => {
    if (!confirm(`Are you sure you want to delete "${runName}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingRunId(runId);
    try {
      await evaluationApi.deleteScoringValidationRun(runId);
      await loadRuns();
    } catch (err: any) {
      console.error('Failed to delete run:', err);
      alert(err.message || 'Failed to delete run');
    } finally {
      setDeletingRunId(null);
    }
  };

  const handleNewRun = () => {
    navigate('/opinions/scoring/new');
  };

  const handleRowClick = (runId: string) => {
    navigate(`/opinions/scoring/${runId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusConfig = (status: string) => {
    return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.Pending;
  };

  const renderProgressBar = (run: ScoringValidationRun) => {
    if (run.status !== 'Running') return null;
    
    const total = run.totalExecutions || 1;
    const completed = run.completedExecutions || 0;
    const failed = run.failedExecutions || 0;
    const progress = (completed / total) * 100;

    return (
      <div className="mt-2">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Progress: {completed}/{total} completed</span>
          <span>{failed} failed</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <AppLayout
      pageTitle="Scoring Validation Runs"
      pageDescription="View and manage scoring validation runs"
    >
      {/* Page Actions Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {autoRefresh && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Auto-refreshing
              </div>
            )}
          </div>
          <button
            onClick={handleNewRun}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">New Validation Run</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div data-component="ScoringValidationListPage" className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div data-flow="loading" className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="text-gray-500">Loading validation runs...</p>
            </div>
          </div>
        ) : error ? (
          <div data-flow="error" className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3 text-red-600">
              <AlertCircle className="w-12 h-12" />
              <p className="font-medium">{error}</p>
              <button
                onClick={() => loadRuns()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : runs.length === 0 ? (
          <div data-flow="empty" className="flex flex-col items-center justify-center h-64 text-gray-500">
            <BarChart3 className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium">No validation runs found</p>
            <p className="text-sm mt-2">Create your first validation run to get started</p>
            <button
              onClick={handleNewRun}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">New Validation Run</span>
            </button>
          </div>
        ) : (
          <div data-flow="results" className="space-y-4">
            {runs.map((run) => {
              const statusConfig = getStatusConfig(run.status);
              const StatusIcon = statusConfig.icon;
              const testName = testNames[run.sourceTestId] || run.sourceTestId;

              return (
                <div
                  key={run.id}
                  className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors p-6 cursor-pointer"
                  onClick={() => handleRowClick(run.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Run Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {run.name}
                        </h3>
                        <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {statusConfig.label}
                        </span>
                      </div>

                      {run.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {run.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <BarChart3 className="w-4 h-4" />
                          {testName}
                        </span>
                        <span>
                          {run.judgeModelProvider}/{run.judgeModelId}
                        </span>
                        <span>
                          Temp: {run.temperature}
                        </span>
                        {run.totalCost != null && (
                          <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-medium">
                            {formatCost(run.totalCost)}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          by {run.createdBy} • {formatDate(run.createdAt)}
                        </span>
                      </div>

                      {/* Progress bar for running runs */}
                      {renderProgressBar(run)}

                      {/* Metrics for completed runs */}
                      {run.status === 'Completed' && run.metrics && (
                        <div className="mt-3 flex flex-wrap gap-4 text-sm">
                          <span className={`px-2 py-1 rounded ${
                            run.metrics.meanAbsoluteError < 1.0 ? 'bg-green-100 text-green-700' :
                            run.metrics.meanAbsoluteError <= 2.0 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            MAE: {run.metrics.meanAbsoluteError.toFixed(2)}
                          </span>
                          <span className={`px-2 py-1 rounded ${
                            run.metrics.pearsonCorrelation > 0.7 ? 'bg-green-100 text-green-700' :
                            run.metrics.pearsonCorrelation >= 0.4 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            Pearson: {run.metrics.pearsonCorrelation.toFixed(3)}
                          </span>
                          <span className="px-2 py-1 rounded bg-gray-100 text-gray-700">
                            Samples: {run.metrics.sampleCount}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleRowClick(run.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        title="View details"
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span className="text-sm font-medium">View</span>
                      </button>
                      
                      <button
                        onClick={() => handleDelete(run.id, run.name)}
                        disabled={deletingRunId === run.id}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete run"
                      >
                        {deletingRunId === run.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
};